// ═══════════════════════════════════════════════════════════════════════
// AWARDS ENGINE v4.1 — regras definitivas
//
// CAMADA 1 — Awards VITALÍCIOS (pontos cumulativos pra sempre)
//   Pontos nunca zeram. O maior award já conquistado é mantido pra vida.
//   Uma venda = 1 ponto. Uma ativação de rede viva = 10 pontos.
//
// CAMADA 2 — Prêmios anuais ACUMULATIVOS (volume R$, zera 1/jan)
//   Elegibilidade aberta: qualquer corretor que bater volume LEVA o prêmio.
//   R$ 2M → iPhone. R$ 3M → +Cruzeiro. R$ 5M → +BYD. (acumulativos)
//
// REGRA 90 DIAS
//   Ativação = corretor cadastrado que fez 1ª venda.
//   Ativação VIVA = ativado que teve venda nos últimos 90 dias.
//   Só ativações vivas contam pontos de rede.
//
// METAS COLETIVAS — Caminho A
//   2 metas independentes por camada (Volume R$ + Ativações)
//   Supermeta = bateu AS DUAS (prêmio maior)
// ═══════════════════════════════════════════════════════════════════════

import { AWARDS, REGRAS_PONTOS, TIMES } from "./data";

// ─── Pontos cumulativos (vitalício) do corretor ──────────────────────
export function calcPontosCorretor(c) {
  const pVendas = (c.vendasDiretas || 0) * REGRAS_PONTOS.vendaDireta;
  const ativosVivos = Math.max(0, (c.ativados || 0) - (c.ativadosInativos90d || 0));
  const pAtivados = ativosVivos * REGRAS_PONTOS.ativacaoRede;
  const pBonus = c.pontosBonus || c.pontos_bonus || 0;
  return {
    pVendas,
    pAtivados,
    pBonus,
    total: pVendas + pAtivados + pBonus,
    ativosVivos,
    ativadosInativos: c.ativadosInativos90d || 0,
  };
}

// ─── Award vitalício (maior já conquistado) ──────────────────────────
export function calcAwardVitalicio(c) {
  const total = calcPontosCorretor(c).total;
  let conquistado = AWARDS[0];
  for (const a of AWARDS) if (total >= a.pts) conquistado = a;
  if (c.awardVitalicio) {
    const salvo = AWARDS.find(a => a.id === c.awardVitalicio);
    if (salvo && salvo.pts > conquistado.pts) return salvo;
  }
  return conquistado;
}

// Alias pra compatibilidade
export const calcAwardAtual = calcAwardVitalicio;

// Award baseado SÓ em quantos pontos tem (para componentes que já calcularam pts)
export function awardPorPontos(total) {
  let conquistado = AWARDS[0];
  for (const a of AWARDS) if (total >= a.pts) conquistado = a;
  return conquistado;
}

// ─── Próximo award e progresso ───────────────────────────────────────
export function calcProximoAward(pontosTotal) {
  const atual = AWARDS.reduce((acc, a) => (pontosTotal >= a.pts ? a : acc), AWARDS[0]);
  const idx = AWARDS.findIndex(a => a.id === atual.id);
  if (idx >= AWARDS.length - 1) return null;
  const prox = AWARDS[idx + 1];
  return {
    atual, prox,
    faltam: prox.pts - pontosTotal,
    pct: Math.min((pontosTotal / prox.pts) * 100, 100),
  };
}

export function isDiamond(c) {
  return calcAwardVitalicio(c).id === "diamond";
}

// ─── Prêmios anuais ACUMULATIVOS ─────────────────────────────────────
const PREMIOS_ANUAIS = [
  { id: "iphone",   nome: "iPhone do ano",                award: "gold",     volMin: 2_000_000 },
  { id: "cruzeiro", nome: "Cruzeiro de luxo (2 pessoas)", award: "platinum", volMin: 3_000_000 },
  { id: "byd",      nome: "Veículo BYD 100% elétrico",    award: "diamond",  volMin: 5_000_000 },
];

export function calcPremiosAnuais(c) {
  const volumeAno = c.vendasValorAno ?? c.vendasValor ?? 0;
  return PREMIOS_ANUAIS
    .filter(p => volumeAno >= p.volMin)
    .map(p => ({ ...p, conquistado: true }));
}

export function calcElegibilidadePremios(c) {
  const volumeAno = c.vendasValorAno ?? c.vendasValor ?? 0;
  return PREMIOS_ANUAIS.map(p => ({
    ...p,
    volumeAtual: volumeAno,
    conquistado: volumeAno >= p.volMin,
    faltaVol: Math.max(0, p.volMin - volumeAno),
    pctProgresso: Math.min((volumeAno / p.volMin) * 100, 100),
  }));
}

// Compat legada
export function calcElegibilidade(c) {
  return calcElegibilidadePremios(c).map(p => ({
    award: p.award,
    nomePremio: p.nome,
    volMin: p.volMin,
    ptsMin: 0,
    atingiuPts: true,
    atingiuVol: p.conquistado,
    elegivel: p.conquistado,
    faltamPts: 0,
    faltamVol: p.faltaVol,
  }));
}

// ─── Agregações ──────────────────────────────────────────────────────
// periodo: "semana" | "mes" | "ano" — qual volume usar (default semana pra times)
export function calcPorTime(corretores, timesCustom = null, periodo = "semana") {
  const valorCampo = periodo === "ano" ? "vendasValorAno" : periodo === "mes" ? "vendasValorMes" : "vendasValorSemana";
  const corretoresPub = corretores.filter(c => c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master" && !c.banido);
  const timesUsar = (timesCustom || TIMES).filter(t => t.ativo !== false);
  const grupos = {};
  for (const t of timesUsar) {
    const membros = corretoresPub.filter(c => c.timeId === t.id);
    if (membros.length === 0) {
      grupos[t.id] = { time: t, membros: [], pts: 0, vendas: 0, valor: 0, cadastrados: 0, ativados: 0, valido: false };
      continue;
    }
    let pts = 0, vendas = 0, valor = 0, cadastrados = 0, ativados = 0;
    membros.forEach(c => {
      const p = calcPontosCorretor(c);
      pts += p.total;
      vendas += c.vendasDiretas || 0;
      valor += c[valorCampo] ?? 0;
      cadastrados += c.cadastrados || 0;
      ativados += p.ativosVivos;
    });
    grupos[t.id] = { time: t, membros, pts, vendas, valor, cadastrados, ativados, valido: membros.length >= 3 };
  }
  return Object.values(grupos).filter(g => g.membros.length > 0);
}

// periodo: "mes" default pra regional
export function calcPorRegional(corretores, periodo = "mes") {
  const valorCampo = periodo === "ano" ? "vendasValorAno" : periodo === "semana" ? "vendasValorSemana" : "vendasValorMes";
  const grupos = {};
  corretores.filter(c => c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master" && !c.banido).forEach(c => {
    const uf = c.uf;
    if (!grupos[uf]) {
      grupos[uf] = { uf, cidades: new Set(), membros: [], pts: 0, vendas: 0, valor: 0, cadastrados: 0, ativados: 0 };
    }
    grupos[uf].cidades.add(c.cidade);
    grupos[uf].membros.push(c);
    const p = calcPontosCorretor(c);
    grupos[uf].pts += p.total;
    grupos[uf].vendas += c.vendasDiretas || 0;
    grupos[uf].valor += c[valorCampo] ?? 0;
    grupos[uf].cadastrados += c.cadastrados || 0;
    grupos[uf].ativados += p.ativosVivos;
  });
  return Object.values(grupos).map(g => ({ ...g, cidades: [...g.cidades] }));
}

export function calcNacional(corretores, periodo = "ano") {
  const valorCampo = periodo === "semana" ? "vendasValorSemana" : periodo === "mes" ? "vendasValorMes" : "vendasValorAno";
  let pts = 0, vendas = 0, valor = 0, cadastrados = 0, ativados = 0;
  const estados = new Set();
  corretores.filter(c => c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master" && !c.banido).forEach(c => {
    const p = calcPontosCorretor(c);
    pts += p.total;
    vendas += c.vendasDiretas || 0;
    valor += c[valorCampo] ?? 0;
    cadastrados += c.cadastrados || 0;
    ativados += p.ativosVivos;
    if (c.uf) estados.add(c.uf);
  });
  return { pts, vendas, valor, cadastrados, ativados, estadosAtivos: estados.size };
}

// ─── Metas coletivas (Caminho A: Volume + Ativações + Supermeta) ─────
export function statusMetaColetiva({ atingidoVol, atingidoAtiv, metaVol, metaAtiv }) {
  const bateuVol = atingidoVol >= metaVol;
  const bateuAtiv = atingidoAtiv >= metaAtiv;
  if (bateuVol && bateuAtiv) return "supermeta";
  if (bateuVol) return "volume";
  if (bateuAtiv) return "ativacoes";
  return "parcial";
}

// Compat legada
export function statusMeta(atingidoRs, atingidoPts, metaRs, supermetaRs, metaPts, supermetaPts) {
  if (atingidoRs >= supermetaRs || atingidoPts >= supermetaPts) return "supermeta";
  if (atingidoRs >= metaRs || atingidoPts >= metaPts) return "meta";
  return "parcial";
}

export function statusMetaSimples(atingido, meta, supermeta) {
  if (atingido >= supermeta) return "supermeta";
  if (atingido >= meta) return "meta";
  return "parcial";
}

// Corretores fora dos aceleradores por enquanto (match por 1º nome, minúsculo)
const ACEL_EXCLUIDOS = ["cauan"];

// ─── Aceleradores ────────────────────────────────────────────────────
export function calcAceleradoresCorretor({ corretor, times, regionais, nacional, metas, aceleradores }) {
  if (!corretor) return { total: 0, detalhes: [] };
  const primeiroNome = (corretor.nome || "").trim().toLowerCase().split(/\s+/)[0];
  if (ACEL_EXCLUIDOS.includes(primeiroNome)) {
    return { total: 0, detalhes: [{ label: "Fora dos aceleradores (temporário)", pct: 0 }], pontosBase: 0, pontosBonus: 0 };
  }
  const p = calcPontosCorretor(corretor);
  const pts = p.total;

  if ((corretor.vendasDiretas || 0) === 0 && p.ativosVivos === 0) {
    return { total: 0, detalhes: [{ label: "Não elegível (sem vendas nem ativações vivas no ciclo)", pct: 0 }] };
  }

  const detalhes = [];
  let bonusPct = 0;

  const meuTime = times.find(t => t.time.id === corretor.timeId);
  // Só aplica acelerador de time se o time tem >= 3 membros (válido)
  if (meuTime && meuTime.valido && metas.time) {
    const st = statusMetaColetiva({
      atingidoVol: meuTime.valor, atingidoAtiv: meuTime.ativados,
      metaVol: metas.time.metaVolume, metaAtiv: metas.time.metaAtivacoes,
    });
    if (st === "supermeta") {
      detalhes.push({ label: "Time bateu supermeta (volume + ativações)", pct: aceleradores.timeSupermeta });
      bonusPct += aceleradores.timeSupermeta;
    } else if (st !== "parcial") {
      detalhes.push({ label: `Time bateu meta de ${st === "volume" ? "volume" : "ativações"}`, pct: aceleradores.timeMeta });
      bonusPct += aceleradores.timeMeta;
    }
  }

  const minhaReg = regionais.find(r => r.uf === corretor.uf);
  if (minhaReg && metas.regional) {
    const st = statusMetaColetiva({
      atingidoVol: minhaReg.valor, atingidoAtiv: minhaReg.ativados,
      metaVol: metas.regional.metaVolume, metaAtiv: metas.regional.metaAtivacoes,
    });
    if (st === "supermeta") {
      detalhes.push({ label: `Regional ${corretor.uf} bateu supermeta`, pct: aceleradores.regionalSupermeta });
      bonusPct += aceleradores.regionalSupermeta;
    } else if (st !== "parcial") {
      detalhes.push({ label: `Regional ${corretor.uf} bateu meta de ${st === "volume" ? "volume" : "ativações"}`, pct: aceleradores.regionalMeta });
      bonusPct += aceleradores.regionalMeta;
    }
  }

  if (metas.nacional) {
    const st = statusMetaColetiva({
      atingidoVol: nacional.valor, atingidoAtiv: nacional.ativados,
      metaVol: metas.nacional.metaVolume, metaAtiv: metas.nacional.metaAtivacoes,
    });
    if (st === "supermeta") {
      detalhes.push({ label: "Nacional bateu supermeta", pct: aceleradores.nacionalSupermeta });
      bonusPct += aceleradores.nacionalSupermeta;
    } else if (st !== "parcial") {
      detalhes.push({ label: `Nacional bateu meta de ${st === "volume" ? "volume" : "ativações"}`, pct: aceleradores.nacionalMeta });
      bonusPct += aceleradores.nacionalMeta;
    }
  }

  // Aceleradores agora são PONTOS fixos (não % dos pontos). total = soma dos pontos de bônus.
  return { total: bonusPct, detalhes, pontosBase: pts, pontosBonus: Math.round(bonusPct) };
}

// ─── Ranking geral ───────────────────────────────────────────────────
export function rankingGeral(corretores) {
  return [...corretores]
    .filter(c => c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master" && !c.banido)
    .map(c => {
      const p = calcPontosCorretor(c);
      const a = calcAwardVitalicio(c);
      const premios = calcPremiosAnuais(c);
      return {
        ...c, pontos: p.total, pVendas: p.pVendas, pAtivados: p.pAtivados,
        ativosVivos: p.ativosVivos, award: a, awardVit: a,
        isDiamond: a.id === "diamond", premiosAno: premios,
      };
    })
    .sort((x, y) => y.pontos - x.pontos);
}

export function posicaoNoAward(corretor, corretores) {
  const rank = rankingGeral(corretores);
  const meuAward = calcAwardVitalicio(corretor).id;
  const doMeuAward = rank.filter(c => c.award.id === meuAward);
  const idx = doMeuAward.findIndex(c => c.id === corretor.id);
  return { posicao: idx + 1, total: doMeuAward.length, awardId: meuAward };
}

// ─── Progresso das Metas Individuais (semanal) ────────────────────────
// Compara volume/ativações DA SEMANA do corretor contra a meta do seu award.
// As metas são definidas pelo admin por award.
export function calcMetasIndividuais(corretor, metasIndividuais) {
  const award = calcAwardVitalicio(corretor);
  const meta = metasIndividuais?.[award.id] || { volume: 0, ativacoes: 0 };

  const volumeAtual = corretor.vendasValorSemana || 0;
  const ativacoesAtuais = corretor.ativacoesSemana != null
    ? corretor.ativacoesSemana
    : 0;

  return {
    award,
    meta,
    volume: {
      atual: volumeAtual,
      meta: meta.volume,
      pct: meta.volume > 0 ? Math.min((volumeAtual / meta.volume) * 100, 100) : 0,
      atingiu: meta.volume > 0 && volumeAtual >= meta.volume,
    },
    ativacoes: {
      atual: ativacoesAtuais,
      meta: meta.ativacoes,
      pct: meta.ativacoes > 0 ? Math.min((ativacoesAtuais / meta.ativacoes) * 100, 100) : 0,
      atingiu: meta.ativacoes === 0 || ativacoesAtuais >= meta.ativacoes,
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// RANKING TEMPORAL (semana / mês / ano)
//
// Prestígio individual. Acelerador de pontos sem custo físico pra Bellanor.
// Usa campos pontosSemana, pontosMes, pontosAno do corretor.
// ═══════════════════════════════════════════════════════════════════════

// Bônus do ranking individual (fixos — não são editáveis pelo admin
// pois são regra essencial do programa conforme regulamento)
export const BONUS_RANKING_INDIVIDUAL = {
  semanaTime:      { pct: 5,  mencaoPublica: false, label: "#1 da semana no Time" },
  semanaRegional:  { pct: 10, mencaoPublica: false, label: "#1 da semana na Regional" },
  semanaNacional:  { pct: 15, mencaoPublica: true,  label: "#1 da semana Nacional", destaque: "Menção de destaque" },
  mesNacional1:    { pct: 15, mencaoPublica: true,  label: "#1 do mês Nacional", destaque: "Menção de destaque" },
  mesNacional2:    { pct: 10, mencaoPublica: true,  label: "#2 do mês Nacional", destaque: "Menção de destaque" },
  mesNacional3:    { pct: 5,  mencaoPublica: true,  label: "#3 do mês Nacional", destaque: "Menção de destaque" },
  anoTop3:         { pct: 0,  mencaoPublica: true,  label: "Top 3 do Ano Nacional",
                     destaque: "Confraternização anual + placa física" },
};

// Limite anti-monopólio: após 3 semanas consecutivas como #1, pula 1 semana
const ANTI_MONOPOLIO_LIMITE = 3;

// Ordena corretores por uma métrica temporal (pontosSemana, pontosMes, pontosAno)
// com critérios de desempate
function ordenarPorMetrica(corretores, metrica) {
  return [...corretores]
    .filter(c => c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master" && !c.banido)
    .filter(c => (c[metrica] || 0) > 0)
    .sort((a, b) => {
      // 1) métrica principal (pontos no período)
      if ((b[metrica] || 0) !== (a[metrica] || 0)) {
        return (b[metrica] || 0) - (a[metrica] || 0);
      }
      // 2) volume R$ do período (usa vendasValorSemana/Mes/Ano com fallback)
      const periodo = metrica.replace("pontos", "").toLowerCase(); // "semana", "mes", "ano"
      const volA = a[`vendasValor${periodo.charAt(0).toUpperCase()}${periodo.slice(1)}`] ?? a.vendasValorAno ?? a.vendasValor ?? 0;
      const volB = b[`vendasValor${periodo.charAt(0).toUpperCase()}${periodo.slice(1)}`] ?? b.vendasValorAno ?? b.vendasValor ?? 0;
      if (volB !== volA) return volB - volA;
      // 3) qtd de vendas diretas totais (proxy)
      return (b.vendasDiretas || 0) - (a.vendasDiretas || 0);
    });
}

// Ranking da semana (todos os corretores, ordenados por pontosSemana)
export function rankingSemanal(corretores) {
  return ordenarPorMetrica(corretores, "pontosSemana");
}

// Ranking da semana dentro de um time específico
export function rankingSemanalTime(corretores, timeId) {
  return rankingSemanal(corretores.filter(c => c.timeId === timeId));
}

// Ranking da semana dentro de uma regional (UF)
export function rankingSemanalRegional(corretores, uf) {
  return rankingSemanal(corretores.filter(c => c.uf === uf));
}

// Ranking do mês nacional (Top 3)
export function rankingMensalNacional(corretores) {
  return ordenarPorMetrica(corretores, "pontosMes");
}

// Ranking do ano nacional (Top 3)
export function rankingAnualNacional(corretores) {
  return ordenarPorMetrica(corretores, "pontosAno");
}

// Verifica se o corretor está em "descanso forçado" por anti-monopólio
// Usa o campo `semanasConsecutivasComo1` do corretor (quantas semanas seguidas ele foi #1)
export function estaEmDescansoAntiMonopolio(corretor, nivel = "nacional") {
  const campo = `semanasComo1${nivel.charAt(0).toUpperCase() + nivel.slice(1)}`;
  const consecutivas = corretor[campo] || 0;
  return consecutivas >= ANTI_MONOPOLIO_LIMITE;
}

// Calcula o bônus de ranking individual aplicável a um corretor no ciclo atual
// Considera anti-monopólio e retorna detalhes p/ exibição
export function calcBonusRankingIndividual(corretor, corretores) {
  if (!corretor) return { total: 0, detalhes: [] };

  const detalhes = [];
  let totalPct = 0;
  const pts = calcPontosCorretor(corretor).total;

  // #1 da semana NACIONAL?
  const rankNac = rankingSemanal(corretores);
  if (rankNac[0]?.id === corretor.id) {
    if (estaEmDescansoAntiMonopolio(corretor, "nacional")) {
      detalhes.push({
        label: "#1 Nacional da semana — descanso anti-monopólio",
        pct: 0,
        tipo: "descanso",
      });
    } else {
      detalhes.push({ ...BONUS_RANKING_INDIVIDUAL.semanaNacional, tipo: "conquista" });
      totalPct += BONUS_RANKING_INDIVIDUAL.semanaNacional.pct;
    }
  }

  // #1 da semana REGIONAL? (só se não é o #1 nacional, pra não duplicar)
  const rankReg = rankingSemanalRegional(corretores, corretor.uf);
  const eh1Reg = rankReg[0]?.id === corretor.id;
  const eh1Nac = rankNac[0]?.id === corretor.id;
  if (eh1Reg && !eh1Nac) {
    if (estaEmDescansoAntiMonopolio(corretor, "regional")) {
      detalhes.push({
        label: `#1 Regional ${corretor.uf} — descanso anti-monopólio`,
        pct: 0, tipo: "descanso",
      });
    } else {
      detalhes.push({ ...BONUS_RANKING_INDIVIDUAL.semanaRegional, label: `#1 da semana na Regional ${corretor.uf}`, tipo: "conquista" });
      totalPct += BONUS_RANKING_INDIVIDUAL.semanaRegional.pct;
    }
  }

  // #1 da semana no TIME? (só se não é o #1 regional)
  if (corretor.timeId) {
    const rankTime = rankingSemanalTime(corretores, corretor.timeId);
    if (rankTime[0]?.id === corretor.id && !eh1Reg && !eh1Nac) {
      if (estaEmDescansoAntiMonopolio(corretor, "time")) {
        detalhes.push({
          label: "#1 do Time — descanso anti-monopólio",
          pct: 0, tipo: "descanso",
        });
      } else {
        detalhes.push({ ...BONUS_RANKING_INDIVIDUAL.semanaTime, tipo: "conquista" });
        totalPct += BONUS_RANKING_INDIVIDUAL.semanaTime.pct;
      }
    }
  }

  // Top 3 do MÊS Nacional?
  const rankMes = rankingMensalNacional(corretores);
  const idxMes = rankMes.findIndex(c => c.id === corretor.id);
  if (idxMes >= 0 && idxMes < 3) {
    const bonus = [
      BONUS_RANKING_INDIVIDUAL.mesNacional1,
      BONUS_RANKING_INDIVIDUAL.mesNacional2,
      BONUS_RANKING_INDIVIDUAL.mesNacional3,
    ][idxMes];
    detalhes.push({ ...bonus, tipo: "conquista" });
    totalPct += bonus.pct;
  }

  return {
    total: totalPct,
    detalhes,
    pontosBonus: Math.round((pts * totalPct) / 100),
  };
}

// Info "ao vivo" do ranking semanal — para exibir no pódio da home do corretor
// Retorna: { top3, usuario: {posicao, pts, proximoAcima}, timeRestante }
export function infoRankingSemanal(corretores, usuarioId) {
  const rank = rankingSemanal(corretores);
  const top3 = rank.slice(0, 3);

  const idx = rank.findIndex(c => c.id === usuarioId);
  const usuario = idx >= 0 ? {
    posicao: idx + 1,
    pts: rank[idx].pontosSemana || 0,
    proximoAcima: idx > 0 ? {
      nome: rank[idx - 1].nome,
      faltam: (rank[idx - 1].pontosSemana || 0) - (rank[idx].pontosSemana || 0),
    } : null,
  } : null;

  // Calcula tempo até fim da semana (domingo 23:59:59)
  const agora = new Date();
  const diaSemana = agora.getDay(); // 0=dom, 1=seg, ... 6=sab
  const diasRestantes = diaSemana === 0 ? 0 : 7 - diaSemana;
  const fimSemana = new Date(agora);
  fimSemana.setDate(fimSemana.getDate() + diasRestantes);
  fimSemana.setHours(23, 59, 59, 999);
  const ms = fimSemana.getTime() - agora.getTime();
  const dias = Math.floor(ms / (1000 * 60 * 60 * 24));
  const horas = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  return { top3, usuario, restante: { dias, horas, formatado: `${dias}d ${horas}h` } };
}
